import { ssoRegisterCmp } from "@/api/user";
import { login, extendRefresh, ssoLogin } from "@/api/auth";
import VueCookies from "vue-cookies";

// 남은 시간(초)을 [분, 초] 2자리 문자열로 변환
function restMinSec(restSec) {
  let nMin = 0;
  let nSec = 0;
  if (restSec > 0) {
    nMin = parseInt(restSec / 60);
    nSec = restSec % 60;
  }
  return [String(nMin).padStart(2, "0"), String(nSec).padStart(2, "0")];
}

// Date를 "yyyyMMddHHmmss" 문자열로 변환
function formatYmdHms(date) {
  return (
    String(date.getFullYear()).padStart(4, "0") +
    String(date.getMonth() + 1).padStart(2, "0") +
    String(date.getDate()).padStart(2, "0") +
    String(date.getHours()).padStart(2, "0") +
    String(date.getMinutes()).padStart(2, "0") +
    String(date.getSeconds()).padStart(2, "0")
  );
}

// "yyyyMMddHHmmss" 문자열을 Date로 변환
function parseYmdHms(value) {
  return new Date(
    parseInt(value.substring(0, 4)),
    parseInt(value.substring(4, 6)) - 1,
    parseInt(value.substring(6, 8)),
    parseInt(value.substring(8, 10)),
    parseInt(value.substring(10, 12)),
    parseInt(value.substring(12, 14))
  );
}

// 인증 관련 쿠키 일괄 제거
function clearAuthCookies() {
  [
    "userId",
    "userNm",
    "userGrade",
    "ndbTkn",
    "loginDate",
    "expireDate",
    "refreshToken",
    "findUserNm",
    "findUserId",
    "bizStat",
    "isSso",
  ].forEach((key) => VueCookies.remove(key));
}

// 다른 탭에 로그아웃을 전파하는 localStorage 신호 키
export const LOGOUT_SIGNAL_KEY = "auth-logout";

// 세션 카운트다운 타이머 핸들 (상태가 아닌 모듈 변수로 보관 → 중복 setInterval 방지)
let sessionIntervalId = null;
function stopSessionTimer() {
  if (sessionIntervalId !== null) {
    clearInterval(sessionIntervalId);
    sessionIntervalId = null;
  }
}

const userStore = {
  namespaced: true,
  state: {
    userId: VueCookies.get("userId") || "",
    userNm: VueCookies.get("userNm") || "",
    userGrade: VueCookies.get("userGrade") || "",
    loginDate: VueCookies.get("loginDate") || "",
    ndbTkn: VueCookies.get("ndbTkn") || "",
    expireDate: VueCookies.get("expireDate") || "",
    bizStat: VueCookies.get("bizStat") || "",
    isSso: VueCookies.get("isSso") || "",
    sessionTime: 0,
    isLogoutFlag: false,
    logoutReason: null, // null | "expired" | "manual"
  },
  getters: {
    isLogin(state) {
      return state.ndbTkn !== "";
    },
    getLogoutReason(state) {
      return state.logoutReason;
    },
    getId(state) {
      return state.userId;
    },
    getUserNm(state) {
      return state.userNm;
    },
    getUserGrade(state) {
      return state.userGrade;
    },
    getUserLoginDate(state) {
      return state.loginDate;
    },
    getNdbTkn(state) {
      return state.ndbTkn;
    },
    getBizStat(state) {
      return state.bizStat;
    },
    getExpireDate(state) {
      return state.expireDate;
    },
    getTime(state) {
      return state.sessionTime;
    },
    getIsLogoutShow(state) {
      return state.isLogoutFlag;
    },
    getSessionTime(state) {
      const [nMin, nSec] = restMinSec(state.sessionTime);
      return nMin + "분 " + nSec + "초";
    },

    getSessionTime2(state) {
      const [nMin, nSec] = restMinSec(state.sessionTime);
      return nMin + ":" + nSec;
    },
    getUserAuthorityCode(state) {
      if (state.userGrade === "PLATINUM") {
        return 1;
      }
      if (state.userGrade === "BASIC") {
        return 2;
      }
      if (state.userGrade === "FREE" || state.userGrade === "GUEST") {
        return 3;
      }
    },
    isSso(state) {
      return state.isSso === "true";
    }
  },
  mutations: {
    SET_ID(state, userId) {
      state.userId = userId;
    },
    SET_USER_NM(state, userNm) {
      state.userNm = userNm;
    },
    SET_USER_GRADE(state, userGrade) {
      state.userGrade = userGrade;
    },
    SET_LOGIN_DATE(state, loginDate) {
      state.loginDate = loginDate;
    },
    SET_ACCESS_TOKEN(state, ndbTkn) {
      state.ndbTkn = ndbTkn;
    },
    CLEAR_ID(state) {
      state.userId = "";
    },
    CLEAR_ATHORITY(state) {
      state.userGrade = "";
    },
    CLEAR_ACCESS_TOKEN(state) {
      state.ndbTkn = "";
    },
    CLEAR_LOGIN_DATE(state) {
      state.loginDate = "";
    },
    SET_BIZ_STAT(state, bizStat) {
      state.bizStat = bizStat;
    },
    SET_IS_SSO(state, isSso) {
      state.isSso = isSso;
    },
    CLEAR_ALL(state) {
      state.userId = "";
      state.userGrade = "";
      state.ndbTkn = "";
      state.loginDate = "";
      state.sessionTime = 0;
      state.bizStat = "";
      state.isSso = "";
    },
    SET_EXPIRE_DATE(state, expireDate) {
      state.expireDate = expireDate;
    },
    SET_SESSION_TIME(state, sessionTime) {
      state.sessionTime = sessionTime
    },
    DISCREMENT_SESSION_TIME(state) {
      state.sessionTime--;
    },
    SET_LOGOUT_SHOW_TRUE(state) {
      state.isLogoutFlag = true;
    },
    SET_LOGOUT_SHOW_FALSE(state) {
      state.isLogoutFlag = false;
    },
    SET_LOGOUT_REASON(state, reason) {
      state.logoutReason = reason;
    },
  },
  actions: {
    async LOGIN({ commit, dispatch }, loginUserData) {
      const { data } = await login(loginUserData);
      commit("SET_ID", data.data.userId);
      commit("SET_USER_NM", data.data.userNm);
      commit("SET_USER_GRADE", data.data.userGrade);
      commit("SET_LOGIN_DATE", data.data.loginDtim);
      commit("SET_ACCESS_TOKEN", data.data.ndbTkn);
      commit("SET_BIZ_STAT", data.data.bizStat);
      VueCookies.set("userId", data.data.userId);
      VueCookies.set("userNm", data.data.userNm);
      VueCookies.set("userGrade", data.data.userGrade);
      VueCookies.set("loginDate", data.data.loginDtim);
      VueCookies.set("ndbTkn", data.data.ndbTkn);
      VueCookies.set("bizStat", data.data.bizStat);
      VueCookies.set("isSso", "false");
      dispatch("CALC_EXPIRE_DATE");
      dispatch("START_SESSION_TIMER");
    },

    async SSO_LOGIN({ commit, dispatch }, ssoLoginRequest) {
      const { data } = await ssoLogin(ssoLoginRequest);
      commit("SET_ID", data.data.userId);
      commit("SET_USER_NM", data.data.userNm);
      commit("SET_USER_GRADE", data.data.userGrade);
      commit("SET_LOGIN_DATE", data.data.loginDtim);
      commit("SET_ACCESS_TOKEN", data.data.ndbTkn);
      commit("SET_BIZ_STAT", data.data.bizStat);
      commit("SET_IS_SSO", "true");
      VueCookies.set("userId", data.data.userId);
      VueCookies.set("userNm", data.data.userNm);
      VueCookies.set("userGrade", data.data.userGrade);
      VueCookies.set("loginDate", data.data.loginDtim);
      VueCookies.set("ndbTkn", data.data.ndbTkn);
      VueCookies.set("bizStat", data.data.bizStat);
      VueCookies.set("isSso", "true");
      dispatch("CALC_EXPIRE_DATE");
      dispatch("START_SESSION_TIMER");
    },

    LOGOUT({ state, commit }, payload) {
      // 이미 로그아웃 상태면 중복 처리 금지 (모달/리다이렉트 중복 방지)
      if (!state.ndbTkn) return;

      stopSessionTimer();
      commit("CLEAR_ALL");
      // 떠있을 수 있는 세션 연장 알림 정리
      commit("SET_LOGOUT_SHOW_FALSE");
      clearAuthCookies();

      commit("SET_LOGOUT_REASON", payload?.reason || "manual");
      //이후 reason이 expired인 경우 App.vue에서 watch로 자동으로 로그인 페이지로 이동

      // 열려 있는 다른 탭에도 로그아웃 전파 (③ 탭 간 동기화)
      try {
        localStorage.setItem(LOGOUT_SIGNAL_KEY, String(Date.now()));
      } catch (e) {
        // localStorage 사용 불가 환경 무시
      }
    },

    // ① 앱 부팅 시: 세션이 만료됐는데 토큰 쿠키만 남아 로그인처럼 보이는 경우 조용히 정리
    INVALIDATE_IF_EXPIRED({ state, commit }) {
      if (!state.ndbTkn) return;
      const expired =
        !state.expireDate ||
        parseYmdHms(state.expireDate).getTime() <= Date.now();
      if (expired) {
        stopSessionTimer();
        commit("CLEAR_ALL");
        clearAuthCookies();
      }
    },

    // ③ 다른 탭에서 온 로그아웃 신호 처리: 재전파/알림 없이 상태만 비운다
    SYNC_LOGOUT({ commit }) {
      stopSessionTimer();
      commit("CLEAR_ALL");
      clearAuthCookies();
    },

    async REFRESH_USER({ commit }, data) {
      commit("SET_ACCESS_TOKEN", data.data.ndbTkn);
      VueCookies.set("userGrade", data.data.userGrade);
      VueCookies.set("ndbTkn", data.data.ndbTkn);
    },

    START_SESSION_TIMER({ commit, state, dispatch }) {
      // 기존 타이머가 있으면 먼저 정리 → 레이아웃 재마운트/재로그인 시 중복 누적 방지
      stopSessionTimer();
      if (!state.ndbTkn) return;

      dispatch("GET_TIME_DIFF");
      sessionIntervalId = setInterval(() => {
        if (state.sessionTime % 10 == 0) {
          dispatch("GET_TIME_DIFF");
        }
        commit("DISCREMENT_SESSION_TIME");
        if (!state.expireDate) {
          dispatch("LOGOUT");
        }

        if (state.sessionTime < 1) {
          stopSessionTimer();
        }
      }, 1000);
    },

    async LOGIN_EXTENSION({ commit, dispatch }, param) {
      await extendRefresh(param?.skipLoading);
      commit("SET_LOGOUT_SHOW_FALSE");
      dispatch("CALC_EXPIRE_DATE");
      dispatch("GET_TIME_DIFF");
    },

    CALC_EXPIRE_DATE({ commit }) {
      const expireDate = formatYmdHms(new Date(Date.now() + 1000 * 60 * 30));
      VueCookies.set("expireDate", expireDate);
      commit("SET_EXPIRE_DATE", expireDate);
    },

    GET_TIME_DIFF({ commit, state }) {
      if (!state.expireDate) {
        commit("SET_SESSION_TIME", 0);
        return;
      }
      const secondsDiff = Math.floor((parseYmdHms(state.expireDate) - new Date()) / 1000);
      commit("SET_SESSION_TIME", secondsDiff);
    },

    LOGOUT_SHOW_TRUE({ commit }) {
      commit("SET_LOGOUT_SHOW_TRUE");
    },
    LOGOUT_SHOW_FALSE({ commit }) {
      commit("SET_LOGOUT_SHOW_FALSE");
    },

    async SSO_CMP_REGISTER({ commit }, formData) {
      const { data } = await ssoRegisterCmp(formData);
      commit("SET_BIZ_STAT", data.data.bizStat);
      commit("SET_USER_GRADE", data.data.userGrade);
      VueCookies.set("bizStat", data.data.bizStat);
      VueCookies.set("userGrade", data.data.userGrade);
      VueCookies.set("ndbTkn", data.data.ndbTkn);
    },
    RESET_LOGOUT_REASON({ commit }) {
      commit("SET_LOGOUT_REASON", null);
    }
  },
};

export default userStore;
